// Generate Fiche PDF for sales orders (Optician Order Form)
import jsPDF from 'jspdf';

export const generateFichePDF = (order, clientName, items = [], payments = []) => {
  // Debug logging
  console.log('Generating Fiche PDF for order:', order.id);
  console.log('Order data:', order);
  console.log('Items:', items);
  console.log('Payments:', payments);
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 15;
  const maxWidth = pageWidth - 2 * margin;
  
  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };
  
  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Pre-computed collections
  const frameItems = items.filter(item => item.item_type === 'monture');
  const lensItems = items.filter(item => item.item_type === 'verre');

  // Company header - reduced spacing
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('EPOPTIQUE', pageWidth / 2, 25, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('OPTICIEN OPTOMETRISTE', pageWidth / 2, 32, { align: 'center' });
  
  doc.setFontSize(9);
  doc.text('0522-22-55-25 / 07-64-200-156', pageWidth / 2, 38, { align: 'center' });
  doc.text('8 RUE ABOU ALWAQT KHALAF, BOURGOGNE CASABLANCA', pageWidth / 2, 42, { align: 'center' });
  
  // Document reference
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`EPC ${order.id}`, pageWidth - margin, 25, { align: 'right' });
  
  let yPosition = 55;
  
  // Customer and order information - compact layout
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('DATE :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(formatDate(order.created_at), margin + 25, yPosition);
  
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('LE NOM COMPLET :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(clientName || '', margin + 45, yPosition);
  
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('DE LA PART DE :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  const clientReferredBy = order.client_referred_by || '';
  doc.text(clientReferredBy || '-', margin + 45, yPosition);
  
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('EXÉCUTÉ PAR :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  const employeeName = order.employee_name || '';
  doc.text(employeeName || '-', margin + 45, yPosition);
  
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('LE PRIX TOTAL :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`${order.total || 0} MAD`, margin + 45, yPosition);
  
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('AVANCE :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  const totalPaid = order.total_paid || payments.reduce((sum, p) => sum + Number(p.amount), 0);
  doc.text(`${totalPaid} MAD`, margin + 25, yPosition);
  
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('RESTE :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  const rest = order.remaining || Math.max(0, (order.total || 0) - totalPaid);
  doc.text(`${rest} MAD`, margin + 25, yPosition);
  
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('DATE DE RECEPTION :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(formatDate(order.created_at), margin + 55, yPosition);
  
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('NUMÉRO DE TELEPHONE :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  const clientPhone = order.client_phone || '';
  doc.text(clientPhone, margin + 65, yPosition);
  
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Commandé :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`EPC ${order.id}`, margin + 30, yPosition);
  
  // Payment methods section
  yPosition += 12;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Modes de paiement :', margin, yPosition);
  yPosition += 6;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  
  // Show payment modes used, or default options
  const paymentModes = [...new Set(payments.map(p => p.mode))];
  if (paymentModes.length > 0) {
    paymentModes.forEach((mode, index) => {
      const modeLabel = mode === 'espece' ? 'ESPECE' : 
                       mode === 'carte_bancaire' ? 'CARTE' : 
                       mode === 'cheque' ? 'CHEQUE' : 
                       mode === 'virement' ? 'VIREMENT' : mode.toUpperCase();
      doc.text(modeLabel, margin + (index * 25), yPosition);
    });
  } else {
    doc.text('ESPECE', margin, yPosition);
    doc.text('CARTE', margin + 25, yPosition);
    doc.text('CHEQUE', margin + 50, yPosition);
    doc.text('VIREMENT', margin + 75, yPosition);
  }
  
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('MUTUELLE :', margin, yPosition);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(order.insurance || 'Aucune', margin + 30, yPosition);

  if (order.order_note && String(order.order_note).trim() !== '') {
    yPosition += 8;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Note commande :', margin, yPosition);
    yPosition += 6;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'italic');
    const noteLines = doc.splitTextToSize(String(order.order_note).trim(), maxWidth - 10);
    noteLines.forEach((line, lineIndex) => {
      doc.text(line, margin + 5, yPosition + (lineIndex * 5.5));
    });
    yPosition += Math.max(0, noteLines.length - 1) * 5.5;
  }
  
  // Optical measurements section
  yPosition += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('MESURES OPTIQUES', margin, yPosition);
  
  yPosition += 10;
  doc.setFontSize(9);
  
  // Frame information
  if (frameItems.length > 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('MONTURES :', margin, yPosition);

    frameItems.forEach((frame, index) => {
      yPosition += index === 0 ? 6 : 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`${index + 1}. ${frame.product_name || ''}`, margin + 5, yPosition);

      yPosition += 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Quantité :', margin + 10, yPosition);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(frame.quantity?.toString() || '1', margin + 35, yPosition);
    });
  }
  
  // Vision measurements
  yPosition += 10;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('VISION DE LOIN :', margin, yPosition);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  
  // Get all optical measurements from sales_orders table
  let leftSph = order.left_sph;
  let rightSph = order.right_sph;
  let leftCyl = order.left_cyl;
  let rightCyl = order.right_cyl;
  let leftAxis = order.left_axis;
  let rightAxis = order.right_axis;
  
  // If not in order, try to get from lens items
  if (!leftSph || !rightSph || !leftCyl || !rightCyl || !leftAxis || !rightAxis) {
    const leftLens = items.find(item => item.item_type === 'verre' && item.side === 'left');
    const rightLens = items.find(item => item.item_type === 'verre' && item.side === 'right');
    leftSph = leftSph || leftLens?.sph || '';
    rightSph = rightSph || rightLens?.sph || '';
    leftCyl = leftCyl || leftLens?.cyl || '';
    rightCyl = rightCyl || rightLens?.cyl || '';
    leftAxis = leftAxis || leftLens?.axe || '';
    rightAxis = rightAxis || rightLens?.axe || '';
  }
  
  // Format the values properly for "sph ( cyl axe )" format
  const formatVisionValues = (sph, cyl, axis) => {
    const sphFormatted = sph ? parseFloat(sph).toFixed(2) : '';
    const cylFormatted = cyl ? parseFloat(cyl).toFixed(2) : '';
    const axisFormatted = axis ? parseFloat(axis).toFixed(2) : '';
    
    if (sphFormatted || cylFormatted || axisFormatted) {
      return `${sphFormatted} ( ${cylFormatted} ${axisFormatted} )`;
    }
    return '';
  };
  
  const leftVision = formatVisionValues(leftSph, leftCyl, leftAxis);
  const rightVision = formatVisionValues(rightSph, rightCyl, rightAxis);
  
  if (leftVision || rightVision) {
    doc.text(`${rightVision} / ${leftVision}`, margin + 45, yPosition);
  } else {
    doc.text('/', margin + 45, yPosition);
  }
  
  yPosition += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('VISION DE PRES :', margin, yPosition);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  
  let leftAddition = order.left_addition;
  let rightAddition = order.right_addition;
  
  // If not in order, try to get from lens items
  if (!leftAddition || !rightAddition) {
    const leftLens = items.find(item => item.item_type === 'verre' && item.side === 'left');
    const rightLens = items.find(item => item.item_type === 'verre' && item.side === 'right');
    leftAddition = leftAddition || leftLens?.addition || '';
    rightAddition = rightAddition || rightLens?.addition || '';
  }
  
  // Format the values properly
  const leftAdditionFormatted = leftAddition ? parseFloat(leftAddition).toFixed(2) : '';
  const rightAdditionFormatted = rightAddition ? parseFloat(rightAddition).toFixed(2) : '';
  
  if (leftAdditionFormatted || rightAdditionFormatted) {
    doc.text(`${rightAdditionFormatted} / ${leftAdditionFormatted}`, margin + 45, yPosition);
  } else {
    doc.text('/', margin + 45, yPosition);
  }
  
  yPosition += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('TYPE DE VERRES :', margin, yPosition);
  
  // Get lens type from items and organize by product
  if (lensItems.length > 0) {
    // Group lenses by product name
    const groupedLenses = {};
    lensItems.forEach(lens => {
      const productName = lens.product_name || 'Sans nom';
      if (!groupedLenses[productName]) {
        groupedLenses[productName] = [];
      }
      groupedLenses[productName].push(lens);
    });
    
    // Display each product group - show product name once with all sides underneath
    Object.entries(groupedLenses).forEach(([productName, lenses], groupIndex) => {
      yPosition += groupIndex === 0 ? 6 : 7;
      
      // Product name (brand) - displayed once per product
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(productName, margin + 5, yPosition);
      
      // Display each lens variant (side) of this product underneath
      // Sort lenses to display droite (right) before gauche (left)
      const sortedLenses = [...lenses].sort((a, b) => {
        if (a.side === 'right' && b.side === 'left') return -1;
        if (a.side === 'left' && b.side === 'right') return 1;
        return 0;
      });
      
      sortedLenses.forEach((lens, variantIndex) => {
        yPosition += 4;
        
        // Side and Quantity on the same line
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Côté :', margin + 10, yPosition);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        const sideLabel = lens.side === 'left' ? 'GAUCHE' : lens.side === 'right' ? 'DROITE' : 'N/A';
        doc.text(sideLabel, margin + 23, yPosition);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Quantité :', margin + 70, yPosition);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(lens.quantity?.toString() || '1', margin + 93, yPosition);
        
        // Display remarque (additional details) below in italic if exists
        if (lens.remarque && lens.remarque.trim() !== '') {
          yPosition += 3;
          doc.setFontSize(8);
          doc.setFont('helvetica', 'italic');
          const remarqueMaxWidth = pageWidth - 2 * margin - 10;
          const lines = doc.splitTextToSize(lens.remarque, remarqueMaxWidth);
          lines.forEach((line, lineIndex) => {
            doc.text(line, margin + 10, yPosition + (lineIndex * 3));
          });
          yPosition += (lines.length - 1) * 3;
        }
      });
    });
  }
  
  // Pupillary distance measurements - compact layout
  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('MESURES PUPILLAIRES :', margin, yPosition);
  
  yPosition += 6;
  doc.setFontSize(9);
  
  // First row: ÉCART DROIT and ÉCART GAUCHE (verre droit)
  doc.setFont('helvetica', 'bold');
  doc.text('ÉCART DROIT (verre droit) :', margin, yPosition);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  if (order.pd_right_verre_droit) {
    doc.text(order.pd_right_verre_droit.toString(), margin + 60, yPosition);
  }
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('ÉCART GAUCHE (verre droit) :', margin + 90, yPosition);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  if (order.pd_left_verre_droit) {
    doc.text(order.pd_left_verre_droit.toString(), margin + 150, yPosition);
  }
  
  yPosition += 5;
  
  // Second row: ÉCART DROIT and ÉCART GAUCHE (verre gauche)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('ÉCART DROIT (verre gauche) :', margin, yPosition);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  if (order.pd_right_verre_gauche) {
    doc.text(order.pd_right_verre_gauche.toString(), margin + 60, yPosition);
  }
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('ÉCART GAUCHE (verre gauche) :', margin + 90, yPosition);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  if (order.pd_left_verre_gauche) {
    doc.text(order.pd_left_verre_gauche.toString(), margin + 150, yPosition);
  }
  
  yPosition += 6;
  
  // Height measurements
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('HAUTEUR 1 :', margin, yPosition);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  if (order.hauteur1) {
    doc.text(order.hauteur1.toString(), margin + 25, yPosition);
  }
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('HAUTEUR 2 :', margin + 70, yPosition);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  if (order.hauteur2) {
    doc.text(order.hauteur2.toString(), margin + 95, yPosition);
  }
  
  
  // Thank you message at the bottom - professional and centered
  yPosition = pageHeight - 30;
  
  // Add a subtle separator line
  doc.setLineWidth(0.3);
  doc.setDrawColor(180, 180, 180);
  doc.line(margin + 30, yPosition - 5, pageWidth - margin - 30, yPosition - 5);
  
  // Thank you message
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bolditalic');
  doc.setTextColor(180, 36, 58); // Brand color #b4243a
  doc.text('EPOPTIQUE vous remercie pour votre confiance', pageWidth / 2, yPosition + 3, { align: 'center' });
  
  // Reset text color
  doc.setTextColor(0, 0, 0);
  
  // Footer
  yPosition += 13;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Document généré le ' + formatDate(new Date()) + ' à ' + formatTime(new Date()), pageWidth / 2, yPosition, { align: 'center' });
  
  // Save the PDF
  doc.save(`Fiche_Commande_${order.id}.pdf`);
  
  // Log success
  console.log('Fiche PDF generated successfully for order:', order.id);
}; 